import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ExternalLink, LogOut, Users as UsersIcon, ChevronDown, Activity, UserCog, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAppAuth } from '@/contexts/AppAuthContext';

const NAV_SECTIONS = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    defaultPath: '/',
    items: [
      { path: '/notes', label: 'Notes', testId: 'subnav-notes' },
      { path: '/move-in-out', label: 'Move in/out', testId: 'subnav-move-in-out' },
      { path: '/housekeeping', label: 'Housekeeping', testId: 'subnav-housekeeping' },
    ],
  },
  {
    key: 'calendar',
    label: 'Calendar',
    defaultPath: '/calendar',
    items: [
      { path: '/tenants', label: 'Tenants', testId: 'subnav-tenants' },
      { path: '/leads', label: 'Leads', testId: 'subnav-leads' },
      { path: '/vacancy', label: 'Vacancy', testId: 'subnav-vacancy' },
      { path: '/properties', label: 'Properties', testId: 'subnav-properties' },
      { path: '/rent-roll', label: 'Rent Roll', testId: 'subnav-rent-roll' },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    defaultPath: '/rent-tracking',
    items: [
      { path: '/rent-tracking', label: 'Rent Tracking', testId: 'subnav-rent-tracking' },
      { path: '/maintenance', label: 'Maintenance', testId: 'subnav-maintenance' },
      { path: '/parking', label: 'Parking', testId: 'subnav-parking' },
    ],
  },
  {
    key: 'finance',
    label: 'Finance',
    defaultPath: '/transactions',
    items: [
      { path: '/transactions', label: 'Transactions', testId: 'subnav-transactions' },
      { path: '/reports', label: 'Reports', testId: 'subnav-reports' },
      { path: '/flexible-reports', label: 'Flexible Reports', testId: 'subnav-flexible-reports' },
      { path: '/deposits', label: 'Deposits', testId: 'subnav-deposits' },
      { path: '/financial-health', label: 'Financial Health', testId: 'subnav-financial-health' },
    ],
  },
];

function findActiveSection(pathname) {
  for (let i = 1; i < NAV_SECTIONS.length; i++) {
    const section = NAV_SECTIONS[i];
    for (const item of section.items) {
      if (pathname === item.path || pathname.startsWith(item.path + '/')) {
        return section;
      }
    }
  }
  // Dashboard section: check explicit paths
  const dashPaths = ['/', '/notes', '/tasks', '/move-in-out', '/housekeeping'];
  for (const p of dashPaths) {
    if (p === '/' ? pathname === '/' : (pathname === p || pathname.startsWith(p + '/'))) {
      return NAV_SECTIONS[0];
    }
  }
  // Also match /calendar to calendar section (hidden route)
  if (pathname === '/calendar' || pathname.startsWith('/calendar/')) {
    return NAV_SECTIONS[1];
  }
  return NAV_SECTIONS[0];
}

function findActiveSubItem(section, pathname) {
  if (!section) return null;
  for (const item of section.items) {
    if (pathname === item.path || pathname.startsWith(item.path + '/')) {
      return item;
    }
  }
  return null;
}

export function TopNavBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const lastVisited = useRef({});
  const subNavRef = useRef(null);

  const activeSection = useMemo(() => findActiveSection(location.pathname), [location.pathname]);
  const activeSubItem = useMemo(() => findActiveSubItem(activeSection, location.pathname), [activeSection, location.pathname]);

  // Track last visited sub-item per section
  useEffect(() => {
    if (activeSection && activeSubItem) {
      lastVisited.current[activeSection.key] = activeSubItem.path;
    } else if (activeSection && location.pathname === '/') {
      // On dashboard home, remember it
      lastVisited.current[activeSection.key] = '/';
    }
  }, [location.pathname, activeSection, activeSubItem]);

  const handleSectionClick = useCallback((section) => {
    setMobileMenuOpen(false);
    // Dashboard and Calendar always go to their main page and clear sub-selection
    if (section.key === 'dashboard' || section.key === 'calendar') {
      navigate(section.defaultPath);
      return;
    }
    const remembered = lastVisited.current[section.key];
    if (remembered) {
      navigate(remembered);
    } else {
      navigate(section.defaultPath);
    }
  }, [navigate]);

  // Close mobile menu on route change
  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  return (
    <div className="fixed top-0 left-0 right-0 z-50" data-testid="top-nav-container">
      {/* Main nav bar - 48px */}
      <nav
        className="h-12 bg-white flex items-center"
        style={{ borderBottom: '0.5px solid #e5e7eb' }}
        data-testid="main-nav-bar"
      >
        <div className="flex items-center h-full w-full px-4 sm:px-6">
          {/* App name */}
          <span className="font-semibold text-[15px] tracking-tight text-gray-900 mr-8 flex-shrink-0">
            Flex Living Miami
          </span>

          {/* Desktop nav items */}
          <div className="hidden md:flex items-center h-full gap-1">
            {NAV_SECTIONS.map((section) => {
              const isActive = activeSection?.key === section.key;
              return (
                <button
                  key={section.key}
                  onClick={() => handleSectionClick(section)}
                  className={`relative h-full px-4 text-[13px] font-medium transition-colors ${
                    isActive
                      ? 'text-gray-900'
                      : 'text-gray-400 hover:text-gray-700'
                  }`}
                  data-testid={`nav-${section.key}`}
                >
                  {section.label}
                  {isActive && (
                    <span className="absolute bottom-0 left-4 right-4 h-[2px] bg-teal-600 rounded-t" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex-1" />

          {/* User pill / logout */}
          <UserMenu />

          {/* Public landing page preview */}
          <a
            href="/landing"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="topbar-landing-button"
          >
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="View Landing Page">
              <Home className="h-4 w-4 text-gray-400" />
            </Button>
          </a>

          {/* Listings external link */}
          <a
            href="/listings"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="topbar-listings-button"
          >
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="View Listings">
              <ExternalLink className="h-4 w-4 text-gray-400" />
            </Button>
          </a>

          {/* Mobile hamburger */}
          <button
            className="md:hidden ml-2 p-1.5 rounded-md hover:bg-gray-100"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <>
          <div className="fixed inset-0 top-12 bg-black/20 z-40 md:hidden" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute top-12 left-0 right-0 bg-white shadow-lg z-50 md:hidden" style={{ borderBottom: '0.5px solid #e5e7eb' }}>
            {NAV_SECTIONS.map((section) => {
              const isActive = activeSection?.key === section.key;
              return (
                <button
                  key={section.key}
                  onClick={() => handleSectionClick(section)}
                  className={`w-full text-left px-6 py-3 text-[14px] font-medium transition-colors border-b border-gray-100 last:border-0 ${
                    isActive ? 'text-teal-700 bg-teal-50' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid={`mobile-nav-${section.key}`}
                >
                  {section.label}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Sub nav bar - 40px */}
      <nav
        className="h-10 bg-white flex items-center"
        style={{ borderBottom: '0.5px solid #e5e7eb' }}
        data-testid="sub-nav-bar"
      >
        <div
          ref={subNavRef}
          className="flex items-center h-full w-full px-4 sm:px-6 gap-1 overflow-x-auto scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {activeSection?.items.map((item) => {
            const isActive = activeSubItem?.path === item.path;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`flex-shrink-0 px-3 py-1.5 rounded-md text-[13px] transition-colors whitespace-nowrap ${
                  isActive
                    ? 'bg-gray-100 text-gray-900 font-medium'
                    : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'
                }`}
                data-testid={item.testId}
              >
                {item.label}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

/* ── User pill with logout / admin menu ──────────────────────────── */
function UserMenu() {
  const { user, isAdmin, logout } = useAppAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  if (!user) return null;
  const initials = (user.display_name || user.name || user.email || 'U').trim().split(/\s+/).map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="relative mr-2" data-testid="topbar-user-menu">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 h-8 px-2 rounded-full hover:bg-gray-100 transition-colors"
        data-testid="topbar-user-pill"
      >
        <span className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-semibold flex items-center justify-center">
          {initials || '·'}
        </span>
        <ChevronDown className="h-3 w-3 text-gray-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-56 bg-white border rounded-xl shadow-lg z-50 py-1.5">
            <div className="px-4 py-2 border-b">
              <p className="text-sm font-medium text-gray-900 truncate">{user.display_name || user.name}</p>
              <p className="text-xs text-gray-400 truncate">{user.email}</p>
              {isAdmin && (
                <span className="inline-block mt-1 px-1.5 py-0.5 text-[9px] font-semibold rounded bg-emerald-100 text-emerald-700 uppercase tracking-wider">Admin</span>
              )}
            </div>
            <button
              className="w-full text-left px-4 py-2 text-[13px] hover:bg-gray-50 flex items-center gap-2 text-gray-700"
              onClick={() => { setOpen(false); navigate('/account'); }}
              data-testid="topbar-account-link"
            >
              <UserCog className="h-4 w-4 text-gray-400" /> Account settings
            </button>
            {isAdmin && (
              <button
                className="w-full text-left px-4 py-2 text-[13px] hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                onClick={() => { setOpen(false); navigate('/admin/users'); }}
                data-testid="topbar-admin-users-link"
              >
                <UsersIcon className="h-4 w-4 text-gray-400" /> Manage users
              </button>
            )}
            {isAdmin && (
              <button
                className="w-full text-left px-4 py-2 text-[13px] hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                onClick={() => { setOpen(false); navigate('/admin/activity'); }}
                data-testid="topbar-admin-activity-link"
              >
                <Activity className="h-4 w-4 text-gray-400" /> Activity log
              </button>
            )}
            <button
              className="w-full text-left px-4 py-2 text-[13px] hover:bg-gray-50 flex items-center gap-2 text-gray-700 border-t"
              onClick={async () => { setOpen(false); await logout(); navigate('/portal-signin'); }}
              data-testid="topbar-logout"
            >
              <LogOut className="h-4 w-4 text-gray-400" /> Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export { NAV_SECTIONS };
